import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Warehouse,
  Zap,
  Sparkles,
  Star,
} from "lucide-react";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const POPULAR_SLUGS = [
  "pack-cartons-demenagement-t3",
  "carton-standard-renforce",
  "carton-penderie-grand",
  "papier-bulle-10m",
];

const Sections = ({ city, products = [] }) => {
  const popular = POPULAR_SLUGS
    .map((s) => products.find((p) => p.slug === s))
    .filter(Boolean);

  return (
    <>
      {/* ======= SECTION 2 — Livraison à domicile ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons de déménagement à domicile à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Oubliez les déplacements contraignants dans les grandes
              surfaces de bricolage et la manutention de liasses de cartons
              encombrantes dans un véhicule inadapté. Nos équipes gèrent
              l&apos;acheminement complet de votre commande directement
              jusqu&apos;à votre adresse à {city.name}.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Logistique intégrée sans intermédiaire",
                body: "Nous bannissons les transporteurs tiers et les services de messagerie généraux. C'est notre propre structure francilienne qui prend en charge vos fournitures, vous garantissant un soin jaloux lors des manipulations et un respect strict de vos contraintes horaires.",
                Icon: Truck,
              },
              {
                title: "Préservation des propriétés physiques",
                body: "Voyageant à l'abri des variations climatiques et de l'humidité au sein de nos fourgons hermétiques, vos modules conservent leur rigidité nominale. Vous ouvrez des colis impeccables, secs et prêts pour une mise en volume immédiate.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Stock & approvisionnement (split layout sombre) ======= */}
      <section className="py-14 md:py-20 bg-[#0f1d2b] text-white relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-24 h-72 w-72 rounded-full bg-[#237cb5]/40 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -right-24 h-72 w-72 rounded-full bg-[#FF7A00]/20 blur-3xl"
        />
        <div className="container-cdm relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
                <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
                Fluidité d&apos;approvisionnement
              </div>
              <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-white">
                Pourquoi notre stock ne fait jamais défaut
              </h2>
              <p className="mt-5 text-[15px] md:text-base text-white/75 leading-relaxed">
                Dans le secteur du déménagement, l&apos;urgence est une
                constante et le manque de matériel au milieu de la nuit
                peut paralyser toute votre organisation. Pour vous éviter
                ce scénario, nous appliquons une politique de stockage
                rigoureuse.
              </p>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {[
                {
                  title: "Infrastructures de stockage massives",
                  body: "Grâce à nos entrepôts de grande capacité stratégiquement positionnés en Île-de-France, nous maintenons en permanence des milliers de références en réserve (cartons, adhésifs, bulles).",
                  Icon: Warehouse,
                },
                {
                  title: "Flux tendu et réactivité absolue",
                  body: "Notre système d'inventaire automatisé anticipe les pics de demande. Que vous ayez besoin de 10 cartons supplémentaires en urgence ou d'une palette complète pour un site professionnel, nous disposons immédiatement de la ressource pour armer vos préparatifs sans le moindre jour de latence.",
                  Icon: Zap,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 bg-white/[0.04] backdrop-blur-sm rounded-2xl border border-white/15 p-5 md:p-6"
                >
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white shrink-0">
                    <Icon className="h-5 w-5" strokeWidth={2.5} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[16px] text-white">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-white/75 leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Les indispensables (4 produits réels) ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
              <Star className="h-3.5 w-3.5" strokeWidth={2.6} />
              Les indispensables
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Les solutions plébiscitées par nos clients clodoaldiens
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Réussir la mise en boîte de ses affaires demande une
              sélection de formats adaptés aux réalités du terrain. À
              {" "}{city.name}, l&apos;architecture des logements et les
              exigences de transport imposent des choix méthodiques. Pour
              vous guider dans vos préparatifs, nous avons réuni
              ci-dessous les modules les plus demandés et les mieux notés
              par les particuliers et les entreprises de la commune.
            </p>
            <p className="mt-3 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Découvrez les références incontournables pour sécuriser,
              sectoriser et transporter l&apos;intégralité de votre
              mobilier en toute confiance.
            </p>
          </div>

          {popular.length === 0 ? (
            <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="aspect-square bg-zinc-100 animate-pulse rounded-2xl"
                />
              ))}
            </div>
          ) : (
            <div
              className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
              data-testid="city-saint-cloud-popular-products"
            >
              {popular.map((p, idx) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  index={idx}
                  hideQty
                  goToProduct
                />
              ))}
            </div>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-saint-cloud-cta-shop"
            >
              <Package className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les cartons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-saint-cloud-cta-packs"
            >
              Voir les kits complets
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-saint-cloud-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
